/**
 * PresentationMode — steps through the board's frames full-screen.
 * Reads frames from CanvasCore (getFrames), zooms to each (zoomToRect), and
 * shows minimal prev/next chrome. Arrow keys + Esc supported. If there are no
 * frames it tells the user to add one and closes.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from '../../../utils/toast';
import { T } from '../../../components/em/ui';

export default function PresentationMode({ api, onClose }) {
  const [frames] = useState(() => api?.getFrames?.() || []);
  const [i, setI] = useState(0);

  const go = useCallback((idx) => {
    const n = frames.length;
    if (!n) return;
    const clamped = Math.max(0, Math.min(n - 1, idx));
    setI(clamped);
    api?.zoomToRect?.(frames[clamped].rect, 60);
  }, [frames, api]);

  // On mount: bail if no frames, else zoom to the first.
  useEffect(() => {
    if (!frames.length) { toast.error('Add a frame first to present'); onClose(); return; }
    api?.zoomToRect?.(frames[0].rect, 60);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight' || e.key === ' ') { e.preventDefault(); go(i + 1); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); go(i - 1); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [i, go, onClose]);

  if (!frames.length) return null;

  const btn = (disabled) => ({
    width: 48, height: 48, borderRadius: 9999, border: 0, cursor: disabled ? 'default' : 'pointer',
    background: 'rgba(15,23,42,0.72)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
    opacity: disabled ? 0.35 : 1, backdropFilter: 'blur(8px)',
  });

  return createPortal(
    <>
      {/* Top bar */}
      <div style={{ position: 'fixed', top: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 3000, display: 'flex', alignItems: 'center', gap: 12, padding: '8px 14px', borderRadius: 9999, background: 'rgba(15,23,42,0.72)', color: '#fff', backdropFilter: 'blur(8px)', font: `600 13px ${T.font}` }}>
        <span>{frames[i]?.name || `Frame ${i + 1}`}</span>
        <span style={{ opacity: 0.6 }}>{i + 1} / {frames.length}</span>
        <button type="button" onClick={onClose} title="Exit (Esc)" style={{ border: 0, background: 'transparent', color: '#fff', cursor: 'pointer', display: 'flex', marginLeft: 4 }}><X size={17} /></button>
      </div>

      {/* Prev / Next */}
      <button type="button" onClick={() => go(i - 1)} disabled={i === 0}
        style={{ ...btn(i === 0), position: 'fixed', left: 20, top: '50%', transform: 'translateY(-50%)', zIndex: 3000 }}>
        <ChevronLeft size={24} />
      </button>
      <button type="button" onClick={() => go(i + 1)} disabled={i === frames.length - 1}
        style={{ ...btn(i === frames.length - 1), position: 'fixed', right: 20, top: '50%', transform: 'translateY(-50%)', zIndex: 3000 }}>
        <ChevronRight size={24} />
      </button>
    </>,
    document.body
  );
}
